// components/ClientArtists.tsx
import React, { useState, useEffect,useRef } from 'react';
import ArtistSearch from './ArtistSearch';
import ArtistAssignment from './ArtistAssignment';
import { Artist, CreateArtistRequest, Country, AssignArtistRequest } from '../../models/artist';
import '../../styles/components.css';
import { IconButton, Tooltip, Typography, Button } from "@mui/material";
import EditDocumentIcon from '@mui/icons-material/EditDocument';
import DeleteIcon from "@mui/icons-material/Delete";
import SearchIcon from '@mui/icons-material/Search';
import PersonAddIcon from '@mui/icons-material/PersonAdd';
import CardButtons from "./CardButtons";

//Standard Api
import axios from "axios";

//Support Safe Api Calls
import { useUser } from '../../components/UserContext';
import { useSafeRequest } from "../../components/useSafeRequest";
import { useIsAuthenticated } from "@azure/msal-react";
import { createApiClient, useApiClient } from "../../components/useApiClient";
import { access } from 'fs';
import { ArtistSource } from '../../models/Artwork';

const ClientArtists: React.FC = () => {

    const BASE_URL = process.env.REACT_APP_API_BASE_URL;

    type dialogModeType = "view" | "edit" | "assign" | "create";

    const [artists, setArtists] = useState<Artist[]>([]);
    const [viewArtist, setViewArtist] = useState<Artist | null>(null);
    const [countries, setCountries] = useState<Country[]>([]);
    const [isPopupOpen, setIsPopupOpen] = useState(false);
    const [isSearchOpen, setIsSearchOpen] = useState(false);
    const [isArtistDialogOpen, setIsArtistDialogOpen] = useState(false);
    const [isLoading, setIsLoading] = useState(true);
    const [dialogMode, setDialogMode] = useState<dialogModeType>("view");

    const { activeClientId, getAccessToken, user } = useUser();
    const previousClientId = useRef<number | null>(activeClientId);

    const clientIdRef = React.useRef<number | null>(null);
    React.useEffect(() => { clientIdRef.current = activeClientId; }, [activeClientId]);

    const isAuthenticated = useIsAuthenticated();

    // *** Setup safe API Calls ***
    const api = React.useMemo(
        () =>
            createApiClient(
                { getAccessToken, getClientId: () => clientIdRef.current },
                { timeoutMs: 12000 }
            ),
        [getAccessToken]
    );
    const { put, post, delete: del } = useApiClient();

    const artistReq = useSafeRequest<any[]>(api, { maxAttempts: 3 });
    const countriesReq = useSafeRequest<any[]>(api, { maxAttempts: 3 });

    const loadAll = async () => {
        try {
            const tasks = [
                artistReq.run({ method: "GET", url: `artists/for-client/${clientIdRef.current}` }),
                countriesReq.run({ method: "GET", url: `countries` }),
            ];

            const [ar, co] = await Promise.allSettled(tasks);

            if (ar.status === "fulfilled") setArtists(ar.value);
            if (co.status === "fulfilled") setCountries(co.value);
        }
        catch (error) {
            console.error('Failed to load artrists & country data:', error);
        } finally {
            setIsLoading(false);
        }
    };

    const loadArtists = async () => {
        try {
            const tasks = [
                artistReq.run({ method: "GET", url: `artists/for-client/${clientIdRef.current}` }),
            ];

            const [ar] = await Promise.allSettled(tasks);

            if (ar.status === "fulfilled") setArtists(ar.value);
        }
        catch (error) {
            console.error('Failed to load artists:', error);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        loadAll();
    }, [clientIdRef]);

    //Artist Selection
    const [selectedArtist, setSelectedArtist] = useState<Artist | null>(null);

    const handleAddArtist = async (artistData: Artist) => {

        try {
            const body = JSON.stringify(artistData);
            const accessToken = await getAccessToken();

            if (accessToken) {
                const response = await axios.post(
                    `${BASE_URL}/artists/add-to-client/${clientIdRef.current}`,
                    body,
                    {
                        headers: {
                            Authorization: `Bearer ${accessToken}`,
                            'X-Client-Id': activeClientId?.toString() || '',
                            "Content-Type": "application/json"
                        }
                    });

                // Refresh the artist list
                await loadArtists();
            }

        } catch (err) {
            console.error('Failed to add', err);
            throw new Error('Failed to add artist');
        }
    };

    const handleUpdateArtist = async (artistData: Artist) => {

        try {
            //const formData = JSON.stringify(artistData);

            const bio = artistData.source == ArtistSource.User ? artistData.biography : artistData.clientBio;

            const formData = new FormData();
            formData.append("ArtistId", artistData.artistId.toString());
            formData.append("ArtistName", artistData.artistName);
            formData.append("BirthDate", artistData.birthDate ?? '');
            formData.append("DeathDate", artistData.deathDate ?? '');
            formData.append("ArtistPeriod", artistData.artistPeriod ?? '');
            formData.append("CountryCode", artistData.countryCode ?? '');
            formData.append("Source", artistData.source.toString());
            formData.append("Biography", bio ?? '');
            formData.append("ClientBio", bio ?? '');


            const accessToken = await getAccessToken();

            if (accessToken) {

                if (artistData.artistId == 0) {
                    await post(`artists/add-to-client/${clientIdRef.current}`, accessToken, formData);
                }
                else {
                    await put(`artists/update-client-link/${artistData.artistId}`, accessToken, formData);
                }

                // Refresh the artist list
                await loadArtists();
            }

        } catch (err) {
            console.error('Failed to add', err);
            throw new Error('Failed to add artist');
        }
    };

    const handleRemoveArtist = async (artistId: number) => {
        if (!window.confirm('Are you sure you want to remove this artist?')) {
            return;
        }

        try {
            const response = await api.delete<any>(`/artists/remove-from-client/${clientIdRef.current}/${artistId}`);

            // Refresh the artist list
            await loadArtists();

        } catch (error) {
            console.error('Failed to remove artist:', error);
        }
    };

    const handleAssignArtist = async (request: AssignArtistRequest) => {

        const body = JSON.stringify(request);
        const accessToken = await getAccessToken();

        if (accessToken) {
            const response = await axios.post(
                `${BASE_URL}/artists/assign-to-client/${clientIdRef.current}`,
                body,
                {
                    headers: {
                        Authorization: `Bearer ${accessToken}`,
                        'X-Client-Id': activeClientId?.toString() || '',
                        "Content-Type": "application/json"
                    }
                });

            await loadArtists();
        }
    };

    const handleArtistSelect = (artist: Artist | null) => {
        setSelectedArtist(artist);
    };

    const handleViewAssignment = (artist: Artist | null) => {
        setDialogMode("edit");
        setViewArtist(artist);
        setIsArtistDialogOpen(true);
    };

    const handleCreateNewArtist = () => {
        setDialogMode("create");
        setViewArtist(null);
        setIsArtistDialogOpen(true);
    };

    if (isLoading) {
        return <div>Loading artists...</div>;
    }

    return (
        <div className="min-h-screen bg-gray-50 py-8">
            <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
                {/* Header */}
                <div className="mb-8">
                    <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between">
                        <div className="mb-4 sm:mb-0">
                            <h1 className="text-3xl font-bold text-gray-900">Artists</h1>
                            <p className="mt-2 text-sm text-gray-600">
                                Manage artists assigned to this client
                            </p>
                        </div>
                        <div className="flex flex-col sm:flex-row space-y-2 sm:space-y-0 sm:space-x-3">
                            <Button
                                variant="contained" color="success" size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setIsSearchOpen(true);
                                }}
                                startIcon={<SearchIcon />}
                            >
                                Search Artists
                            </Button>
                            <Button
                                variant="contained" size="small"
                                onClick={(e) => {
                                    e.stopPropagation();
                                    handleCreateNewArtist();
                                }}
                                startIcon={<PersonAddIcon />}
                            >
                                Add New Artist
                            </Button>
                        </div>
                    </div>
                </div>

                {/* Artist Grid */}
                {artists.length > 0 ? (
                    <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 xl:grid-cols-5 gap-6">
                        {artists.map(artist => (
                            <ArtistCard
                                key={artist.artistId}
                                artist={artist}
                                onRemove={() => handleRemoveArtist(artist.artistId)}
                                onEdit={() => handleViewAssignment(artist)}
                                artworkCount={artist.artworkCount || 0}
                            />
                        ))}
                    </div>
                ) : (
                        <EmptyState onAddArtist={() => handleCreateNewArtist()} />
                )}

                {/* Popup Modals */}
                <ArtistSearch
                    isOpen={isSearchOpen}
                    onClose={() => setIsSearchOpen(false)}
                    onAssignArtist={handleAssignArtist}
                    onArtistSelect={handleArtistSelect}
                    mode="assign"
                />

                <ArtistAssignment
                    open={isArtistDialogOpen}
                    mode={dialogMode}
                    artist={viewArtist}
                    onUpdate={handleUpdateArtist}
                    onClose={() => setIsArtistDialogOpen(false)}
                    countries={countries}
                    existingArtists={artists}
                />
            </div>
        </div>
    );
};


// Artist Card Component
interface ArtistCardProps {
    artist: Artist;
    onRemove: () => void;
    onEdit: () => void;
    artworkCount: number;
}

const ArtistCard: React.FC<ArtistCardProps> = ({ artist, onRemove, onEdit, artworkCount }) => {
    const [showFullBio, setShowFullBio] = useState(false);

    const toggleBio = () => setShowFullBio(!showFullBio);

    return (
        <div className="bg-white rounded-lg shadow-md hover:shadow-lg transition-shadow duration-200 overflow-hidden">
            <div className="p-6">
                {/* Header */}
                <div className="flex justify-between items-start mb-3">
                    <h3 className="text-lg font-semibold text-gray-900 truncate">
                        {artist.artistName}
                    </h3>                   
                    <CardButtons
                        onEdit={onEdit}
                        onRemove={onRemove}
                        artworkCount={artworkCount}
                    />
                </div>

                {/* Meta Information */}
                <div className="space-y-2 mb-4">
                    {artist.countryName && (
                        <div className="flex items-center text-sm text-gray-600">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17.657 16.657L13.414 20.9a1.998 1.998 0 01-2.827 0l-4.244-4.243a8 8 0 1111.314 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 11a3 3 0 11-6 0 3 3 0 016 0z" />
                            </svg>
                            {artist.countryName}
                        </div>
                    )}

                    {artist.artistPeriod && (
                        <div className="flex items-center text-sm text-gray-600">
                            <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                            </svg>
                            Artist Dates: {artist.artistPeriod}
                        </div>
                    )}

                </div>

                {/* Biography */}
                {artist.biography && (
                    <div className="mb-4">
                        <p className={`text-sm text-gray-700 ${!showFullBio ? 'line-clamp-3' : ''}`}>
                            {artist?.biography && (
                                <Typography variant="body2" sx={{ mt: 1, whiteSpace: "pre-line" }}>
                                    {artist.biography}
                                </Typography>
                            )}

                            {artist?.clientBio && (
                                <Typography
                                    variant="body2"
                                    sx={{
                                        mt: artist.biography ? 1 : 0,
                                        fontStyle: "italic",
                                        color: "text.secondary",
                                        whiteSpace: "pre-line",
                                    }}
                                >
                                    — {artist.clientBio}
                                </Typography>
                            )}

                        </p>
                        {artist.biography.length > 150 && (
                            <button
                                onClick={toggleBio}
                                className="mt-2 text-sm text-blue-600 hover:text-blue-800 font-medium"
                            >
                                {showFullBio ? 'Show less' : 'Read more'}
                            </button>
                        )}
                    </div>
                )}

                {/* Source Badge */}
                <div className="flex justify-between items-center">
                    <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${artist.source === 1
                            ? 'bg-purple-100 text-purple-800'
                            : 'bg-green-100 text-green-800'
                        }`}>
                        {artist.source === 1 ? 'System Artist' : 'User Artist'}
                    </span>
                </div>

                {/* Used in Artworks Badge */}
                <div>
                    {artist.artworkCount != null && artist.artworkCount > 0 && (
                        <p>Artworks: {artist.artworkCount ?? 0}</p>
                    )}
                </div>
            </div>
        </div>
    );
};

// Empty State Component
interface EmptyStateProps {
    onAddArtist: () => void;
}

const EmptyState: React.FC<EmptyStateProps> = ({ onAddArtist }) => (
    <div className="text-center py-12">
        <svg className="mx-auto h-24 w-24 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1} d="M11 4a2 2 0 114 0v1a1 1 0 001 1h3a1 1 0 011 1v3a1 1 0 01-1 1h-1a2 2 0 100 4h1a1 1 0 011 1v3a1 1 0 01-1 1h-3a1 1 0 01-1-1v-1a2 2 0 10-4 0v1a1 1 0 01-1 1H7a1 1 0 01-1-1v-3a1 1 0 00-1-1H4a2 2 0 110-4h1a1 1 0 001-1V7a1 1 0 011-1h3a1 1 0 001-1V4z" />
        </svg>
        <h3 className="mt-4 text-lg font-medium text-gray-900">No artists assigned</h3>
        <p className="mt-2 text-sm text-gray-500">
            Get started by adding a new artist or searching existing ones.
        </p>
        <div className="mt-6">
            <button
                onClick={onAddArtist}
                className="inline-flex items-center px-4 py-2 border border-transparent text-sm font-medium rounded-md shadow-sm text-white bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500"
            >
                <svg className="w-4 h-4 mr-2" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
                </svg>
                Add Your First Artist
            </button>
        </div>
    </div>
);



export default ClientArtists;